import { createContext, useContext, useState, ReactNode } from "react";

type Notification = {
	id: string;
	message: string;
	type: "success" | "info" | "warning";
	icon: string;
};

type NotificationContextType = {
	notifications: Notification[];
	addNotification: (notification: Omit<Notification, "id">) => void;
	removeNotification: (id: string) => void;
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
	const [notifications, setNotifications] = useState<Notification[]>([]);

	const addNotification = ({ message, type, icon }: Omit<Notification, "id">) => {
		const id = Date.now().toString();
		setNotifications((prev) => [
			...prev,
			{
				id,
				message,
				type,
				icon,
			},
		]);

		// Auto remove after 5 seconds
		setTimeout(() => {
			removeNotification(id);
		}, 5000);
	};

	const removeNotification = (id: string) => {
		setNotifications((prev) => prev.filter((notif) => notif.id !== id));
	};

	return (
		<NotificationContext.Provider
			value={{ notifications, addNotification, removeNotification }}>
			{children}
		</NotificationContext.Provider>
	);
};

export const useNotifications = () => {
	const context = useContext(NotificationContext);
	if (!context) {
		throw new Error("useNotifications must be used within a NotificationProvider");
	}
	return context;
};
